import { amlConnectedColumn } from '@/shared/grids/columns/amlConnectedColumn';
import { amlRiskLevelBadgeTemplate } from '@/shared/grids/columns/amlRiskLevelColumn';
import { booleanColumn } from '@/shared/grids/columns/booleanColumn';
import { customFieldColumns } from '@/shared/grids/columns/customFieldColumns';
import { dateColumn } from '@/shared/grids/columns/dateColumn';
import { hiddenColumn } from '@/shared/grids/columns/hiddenColumn';
import { idColumn } from '@/shared/grids/columns/idColumn';
import { notesExcerptColumn } from '@/shared/grids/columns/notesExcerptColumn';
import { notesIconColumn } from '@/shared/grids/columns/notesIconColumn';
import { pendingRequestCountColumn } from '@/shared/grids/columns/pendingRequestCountColumn';
import { relationshipTypeColumn } from '@/shared/grids/columns/relationshipTypeColumn';
import {
  completionDueTaskCountColumn,
  deadlineDateDueTaskCountColumn,
  startDueTaskCountColumn,
  uncompletedTaskCountColumn,
} from '@/shared/grids/columns/taskCountColumn';
import { textColumn } from '@/shared/grids/columns/textColumn';
import { AmlRiskLevel, ClientType, MtdItQuarters } from '@/shared/types/enum';
import { GridSettings } from '@/shared/types/grid';
import { enumOptions } from '@/shared/utils/enum';

import { IndividualView } from '@prisma/client';

export const individualGrid: GridSettings<IndividualView> = {
  defaultSort: {
    name: 'asc',
  },
  extraSelect: ['mtdItStartOn'],
  columns: [
    // Hidden columns
    idColumn(),
    hiddenColumn({
      field: 'serviceIds',
    }),
    hiddenColumn({
      field: 'dueTimeFrames',
    }),
    hiddenColumn({
      field: 'assignedUserIds',
    }),
    hiddenColumn({
      field: 'linkedOrganisationIds',
    }),
    // Visible columns
    notesIconColumn({
      field: 'hasNotes',
    }),
    relationshipTypeColumn({
      field: 'relationshipType',
      clientType: ClientType.Individual,
    }),
    textColumn({
      header: 'Reference',
      field: 'internalReference',
      visible: false,
    }),
    textColumn({
      header: 'First Name',
      field: 'givenName',
    }),
    textColumn({
      header: 'Last Name',
      field: 'surname',
    }),
    dateColumn({
      header: 'DOB',
      field: 'birthDate',
      visible: false,
    }),
    booleanColumn({
      header: 'CH Pers Code Present',
      field: 'companiesHousePersonalCode',
      visible: false,
    }),
    textColumn({
      header: 'UTR Number',
      field: 'utr',
      visible: false,
    }),
    textColumn({
      header: 'NI Number',
      field: 'nationalInsuranceNumber',
      visible: false,
    }),
    textColumn({
      header: 'Passport Number',
      field: 'passportNumber',
      visible: false,
    }),
    textColumn({
      header: 'Phone 1',
      field: 'phone1',
    }),
    textColumn({
      header: 'Phone 2',
      field: 'phone2',
      visible: false,
    }),
    textColumn({
      header: 'Email',
      field: 'emailAddress',
    }),
    dateColumn({
      header: 'Registered as Tax Agent',
      field: 'registeredAsTaxAgentDate',
      visible: false,
    }),
    dateColumn({
      header: 'Tax Return Start From',
      field: 'tradingStartDate',
      visible: false,
    }),
    booleanColumn({
      header: 'Tax Return Required',
      field: 'taxReturnRequired',
    }),
    dateColumn({
      header: 'Tax Return Last Completed',
      field: 'taxReturnLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Tax Return Period End',
      field: 'taxReturnPeriodEndDate',
      visible: false,
    }),
    dateColumn({
      header: 'Tax Return Due',
      field: 'taxReturnDueDate',
      due: true,
    }),
    dateColumn({
      header: 'MTD IT Start Date',
      field: 'mtdItStartOn',
      visible: false,
    }),
    textColumn({
      header: 'MTD IT Quarters',
      field: 'mtdItQuarters',
      visible: false,
      template:
        '${if(mtdItStartOn)}${window.formatters.formatMtdItQuarters(mtdItQuarters)}${else}<span class="text-more-muted">Not set</span>${/if}',
      extra: {
        filter: {
          options: enumOptions(MtdItQuarters),
        },
      },
    }),
    dateColumn({
      header: 'MTD IT Last Made Up To',
      field: 'mtdItLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'MTD IT Period End',
      field: 'mtdItPeriodEndDate',
      visible: false,
    }),
    dateColumn({
      header: 'MTD IT Update Due',
      field: 'mtdItUpdateDueDate',
      due: true,
    }),
    dateColumn({
      header: 'MTD IT Final Period',
      field: 'mtdItExitOn',
      visible: false,
    }),
    uncompletedTaskCountColumn({
      linkIdField: 'clientId',
    }),
    startDueTaskCountColumn({
      linkIdField: 'clientId',
      visible: false,
    }),
    completionDueTaskCountColumn({
      linkIdField: 'clientId',
      visible: false,
    }),
    deadlineDateDueTaskCountColumn({
      linkIdField: 'clientId',
      visible: false,
    }),
    pendingRequestCountColumn(),
    dateColumn({
      header: 'Added',
      field: 'createdTime',
      visible: false,
    }),
    dateColumn({
      header: 'Engagement Signed',
      field: 'engagementSignedDate',
      visible: false,
      emptyText: 'Not signed',
      emptyClass: 'text-danger',
    }),
    dateColumn({
      header: 'Last AML Risk Assessment',
      field: 'amlLastCheckedDate',
      visible: false,
      emptyText: 'Never',
      emptyClass: 'text-danger',
      headerTemplate: 'Last AML RA',
    }),
    dateColumn({
      header: 'AML Risk Assessment Due',
      field: 'amlCheckDueDate',
      visible: false,
      due: true,
      emptyText: 'Never',
      emptyClass: 'text-danger',
      headerTemplate: 'AML RA Due',
    }),
    textColumn({
      header: 'AML Risk Level',
      field: 'amlRiskLevel',
      visible: false,
      template: amlRiskLevelBadgeTemplate,
      extra: {
        filter: {
          options: enumOptions(AmlRiskLevel),
        },
      },
    }),
    textColumn({
      header: 'AML Notes',
      field: 'amlNotes',
      visible: false,
    }),
    amlConnectedColumn({
      field: 'xamaAccountId',
    }),
    textColumn({
      header: 'Partner',
      field: 'partnerId',
      extra: {
        displayField: 'partnerName',
        sortField: 'partnerName',
        filter: {
          lookup: 'MemberView',
        },
      },
    }),
    textColumn({
      header: 'Account Manager',
      field: 'accountManagerId',
      extra: {
        displayField: 'accountManagerName',
        sortField: 'accountManagerName',
        filter: {
          lookup: 'MemberView',
        },
      },
    }),

    notesExcerptColumn({
      field: 'notesExcerpt',
    }),
    dateColumn({
      header: 'Archived',
      field: 'archivedTime',
      visible: false,
    }),
    ...customFieldColumns(),
  ],
};
